import { useEffect, useState } from 'react';
import { ApiService } from '../../../services/ApiService';
import type { ActiveAlert } from '../../../types/Alert';

interface Props {
    onLoadStrategy?: (strategyJson: string, name: string, monitorId: number) => void;
}
interface AlertHistory extends ActiveAlert {
    Status: number;
}

export function ActiveAlertsOverview({ onLoadStrategy }: Props) {
    const [currentlyActive, setCurrentlyActive] = useState<ActiveAlert[]>([]);
    const [alertHistory, setAlertHistory] = useState<AlertHistory[]>([]);
    const [loading, setLoading] = useState(true);
    const [heartbeat, setHeartbeat] = useState<string | null>(null);

    const fetchHeartbeat = async () => {
        try {
            const json = await ApiService.getHeartbeat();
            if (json.status) setHeartbeat(json.status);
        } catch (e) {
            console.error("Failed to fetch heartbeat", e);
        }
    };

    const fetchCurrentlyActive = async () => {
        try {
            const json = await ApiService.getCurrentlyActiveAlerts();
            if (json.data) setCurrentlyActive(json.data as ActiveAlert[]);
        } catch (e) {
            console.error('Failed to fetch currently active alerts', e);
        }
    };

    const fetchAlertHistory = async () => {
        try {
            const json = await ApiService.getActiveAlerts();
            if (json.data) setAlertHistory(json.data as AlertHistory[]);
        } catch (e) {
            console.error('Failed to fetch alert history', e);
        } finally {
            setLoading(false);
        }
    };

    const refreshAll = () => {
        setLoading(true);
        Promise.all([fetchCurrentlyActive(), fetchAlertHistory(), fetchHeartbeat()])
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        refreshAll();
        const interval = setInterval(refreshAll, 30000); // Poll every 30s
        return () => clearInterval(interval);
    }, []);

    if (loading) return <div style={{ padding: '2rem' }}>Loading alerts...</div>;

    return (
        <div style={{ padding: '2rem', height: '100%', overflowY: 'auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                <h2 style={{ margin: 0 }}>🚨 Alerts</h2>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {heartbeat && (() => {
                        const isStale = (new Date().getTime() - new Date(heartbeat).getTime()) > 600000;
                        return (
                            <span style={{
                                fontSize: '0.8rem',
                                color: isStale ? 'var(--monitor-heartbeat-stale-fg)' : 'var(--monitor-heartbeat-ok-fg)',
                                backgroundColor: isStale ? 'var(--monitor-heartbeat-stale-bg)' : 'var(--monitor-heartbeat-ok-bg)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                border: '1px solid currentColor'
                            }}>
                                Runner Heartbeat: {new Date(heartbeat).toLocaleTimeString()}
                            </span>
                        );
                    })()}
                    <button
                        onClick={refreshAll}
                        disabled={loading}
                        style={{
                            padding: '0.4rem 0.8rem',
                            borderRadius: '4px',
                            backgroundColor: 'var(--monitor-refresh-bg)',
                            color: 'white',
                            border: 'none',
                            cursor: loading ? 'progress' : 'pointer',
                            fontSize: '0.9rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                        }}
                    >
                        {loading ? 'Refreshing...' : '🔄 Refresh'}
                    </button>
                </div>
            </div>

            {/* Two-column grid layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginTop: '1rem' }}>

                {/* Left Panel: Currently Active Alerts */}
                <div>
                    <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--accent-danger)' }}>⚠️ Currently Active ({currentlyActive.length})</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--surface-glass-border)' }}>
                                <th style={{ padding: '8px' }}>Time</th>
                                <th style={{ padding: '8px' }}>Strategy</th>
                                <th style={{ padding: '8px' }}>Value</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentlyActive.map((alert) => (
                                <tr key={alert.ID} style={{ borderBottom: '1px solid var(--surface-glass-border)', backgroundColor: 'var(--accent-warning-subtle)' }}>
                                    <td style={{ padding: '8px' }}>{new Date(alert.TriggeredAt).toLocaleString()}</td>
                                    <td style={{ padding: '8px', fontWeight: 'bold' }}>{alert.StrategyName}</td>
                                    <td style={{ padding: '8px' }}>
                                        {typeof alert.Value === 'number'
                                            ? alert.Value.toFixed(4)
                                            : Number(alert.Value).toFixed(4)}
                                    </td>
                                    <td style={{ padding: '8px', textAlign: 'center' }}>
                                        <button
                                            onClick={() => alert.StrategyJson && onLoadStrategy?.(alert.StrategyJson, alert.StrategyName || '', alert.StrategyID)}
                                            title="Load Strategy to Workspace"
                                            style={{
                                                backgroundColor: 'var(--monitor-load-bg)',
                                                color: 'white',
                                                border: 'none',
                                                borderRadius: '4px',
                                                padding: '4px 8px',
                                                cursor: 'pointer',
                                                fontSize: '0.8rem'
                                            }}
                                        >
                                            📥 Load
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {currentlyActive.length === 0 && (
                                <tr>
                                    <td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        No currently active alerts. All is quiet! ✅
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Right Panel: Alert History (30 days) */}
                <div>
                    <h3 style={{ marginTop: 0, marginBottom: '1rem', color: 'var(--text-muted)' }}>📜 History (Last 30 Days)</h3>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ textAlign: 'left', borderBottom: '2px solid var(--surface-glass-border)' }}>
                                <th style={{ padding: '8px' }}>Time</th>
                                <th style={{ padding: '8px' }}>Strategy</th>
                                <th style={{ padding: '8px' }}>Status</th>
                                <th style={{ padding: '8px', textAlign: 'center' }}>Action</th>
                            </tr>
                        </thead>
                        <tbody>
                            {alertHistory.map((alert) => {
                                const isActive = alert.Status === 1;
                                return (
                                    <tr key={alert.ID} style={{ borderBottom: '1px solid var(--surface-glass-border)' }}>
                                        <td style={{ padding: '8px' }}>{new Date(alert.TriggeredAt).toLocaleString()}</td>
                                        <td style={{ padding: '8px' }}>{alert.StrategyName}</td>
                                        <td style={{ padding: '8px' }}>
                                            <span style={{
                                                display: 'inline-block',
                                                padding: '2px 8px',
                                                borderRadius: '12px',
                                                fontSize: '0.75rem',
                                                fontWeight: 'bold',
                                                backgroundColor: isActive ? 'var(--accent-danger-subtle)' : 'var(--accent-success-subtle)',
                                                color: isActive ? 'var(--accent-danger)' : 'var(--accent-success)',
                                                border: isActive ? '1px solid var(--accent-danger)' : '1px solid var(--accent-success)'
                                            }}>
                                                {isActive ? '🔴 Active' : '⚪ Resolved'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '8px', textAlign: 'center' }}>
                                            <button
                                                onClick={() => alert.StrategyJson && onLoadStrategy?.(alert.StrategyJson, alert.StrategyName || '', alert.StrategyID)}
                                                title="Load Strategy to Workspace"
                                                style={{
                                                    backgroundColor: 'var(--monitor-load-bg)',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '4px',
                                                    padding: '4px 8px',
                                                    cursor: 'pointer',
                                                    fontSize: '0.8rem'
                                                }}
                                            >
                                                📥 Load
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                            {alertHistory.length === 0 && (
                                <tr>
                                    <td colSpan={4} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
                                        No alerts triggered in the last 30 days.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
}


